import React, { useState } from 'react';
import useTranslation from '../../../../util/hooks/useTranslation';
import Page from '../../../Common/Page';
import Search from '../../Components/Search';
import Table from '../../../Common/Table';
import Card from '../../../Common/Card';
import useAsync from '../../../../util/hooks/useAsync';
import useColumns from '../../../../util/hooks/useColumns';
import * as column from '../../util/grid/column';
import RentalStatusSelect from '../Components/RentalStatusSelect';
import DepartmentSelect from '../Components/DepartmentSelect';
import { useSelector } from 'react-redux';
import { getBorrowedAssetListApi, updateAssetRentalStatusApi } from '../../../../api/asset';

const AssetImport = () => {
    const t = useTranslation('AssetImport');

    const { categoryList } = useSelector(state => state.CategoryInfo);
    const { groupId, groupNum } = useSelector(state => state.UserInfo.userInfo);

    const [tableKey, setTableKey] = useState(0);
    const [assetImportList, setAssetImportList] = useState({
        totalCount: 0,
        totalPage: 1,
        rows: [],
    });
    const [selectedSearchOption, setSelectedSearchOption] = useState({
        lendingDepartment: [],
        rentalStatus: ['allocation', 'check_out', 'confirm_check_out'],
    });

    const columns = useColumns(
        [
            column.assetRentalRequestNum({
                width: 90,
            }),
            column.applicationDate(),
            column.category({
                Cell: ({ value }) => {
                    const category = categoryList.find(({ categoryCode }) => categoryCode === value);
                    if (category) {
                        return <div>{category.categoryNamePath}</div>;
                    } else {
                        return <div>-</div>;
                    }
                },
            }),
            column.assetName(),
            column.assetNumber(),
            column.assetRentalRequestDepart({
                accessor: 'usingDepName',
            }),
            column.assetRentalDepartment({
                accessor: 'ownDepName',
            }),
            column.rentalStatus({
                Cell: ({ value }) => {
                    if (value) {
                        return <div>{t(value, 'AssetRentalRequest')}</div>;
                    } else {
                        return <div>{t('allocation', 'AssetRentalRequest')}</div>;
                    }
                },
            }),
            column.action({
                width: 280,
                Cell: ({ row }) => {
                    const { targetNum, status, reqNum } = row.original;

                    if (status === 'check_out') {
                        return (
                            <div className={'table-btn-container'}>
                                <button
                                    onClick={() =>
                                        updateAssetRentalStatus({
                                            reqNum: reqNum,
                                            targetNum: targetNum,
                                            status: 'confirm_check_out',
                                        })
                                    }
                                    className="pnt-txt txt-border txt-btn-org"
                                    type={'button'}
                                >
                                    {t('confirm_check_out', 'AssetRentalRequest')}
                                </button>
                            </div>
                        );
                    } else if (status === 'confirm_check_out') {
                        return (
                            <div className={'table-btn-container'}>
                                <button
                                    onClick={() =>
                                        updateAssetRentalStatus({
                                            reqNum: reqNum,
                                            targetNum: targetNum,
                                            status: 'check_in',
                                        })
                                    }
                                    className="pnt-txt txt-border txt-btn-org"
                                    type={'button'}
                                >
                                    {t('check_in', 'AssetRentalRequest')}
                                </button>
                            </div>
                        );
                    } else {
                        return <div />;
                    }
                },
            }),
        ],
        null,
        [categoryList],
    );

    // 대여상태가 지정, 반출, 반출확인인 자산만 먼저 호출
    const { promise: getBorrowedAssetList, state: assetImportListInfo } = useAsync({
        promise: getBorrowedAssetListApi,
        fixedParam: { pageSize: 20, usingDepNums: groupNum },
        param: { statusList: 'allocation,check_out,confirm_check_out' },
        keepState: true,
        immediate: true,
        resolve: res => {
            setAssetImportList(res);
        },
    });

    // 자산 상태 변경
    const { promise: updateAssetRentalStatus } = useAsync({
        promise: updateAssetRentalStatusApi,
        resolve: () => {
            getBorrowedAssetList();
        },
    });

    const handleSearchClick = searchInput => {
        setTableKey(prevState => prevState + 1);
        getBorrowedAssetList({
            keyword: searchInput,
            statusList: selectedSearchOption.rentalStatus.length ? selectedSearchOption.rentalStatus.join(',') : null,
            ownDepNums: selectedSearchOption.lendingDepartment.length
                ? selectedSearchOption.lendingDepartment.join(',')
                : null,
        });
    };

    const handleRefreshClick = () => {
        setTableKey(prevState => prevState + 1);
        setSelectedSearchOption({
            lendingDepartment: [],
            rentalStatus: ['allocation', 'check_out', 'confirm_check_out'],
        });
        getBorrowedAssetList();
    };

    const handlePageChange = pageIndex => {
        if (assetImportListInfo.request) {
            getBorrowedAssetList({ ...assetImportListInfo.request, page: pageIndex });
        } else {
            getBorrowedAssetList({
                page: pageIndex,
                statusList: 'allocation,check_out,confirm_check_out',
            });
        }
    };

    return (
        <Page menuPath={[t('Asset Rental Management', 'Menu'), t('Borrowed Assets', 'Menu')]}>
            <div className="grid-layout-container">
                <Search
                    searchInputPlaceholder={t('Please enter the asset name or asset id.')}
                    handleSearchClick={handleSearchClick}
                    handleRefreshClick={handleRefreshClick}
                >
                    <DepartmentSelect
                        title={t('Lending Department', 'AssetRentalManagement')}
                        logInUserDepartment={groupId}
                        selectedOption={selectedSearchOption.lendingDepartment}
                        onChange={selected => {
                            setSelectedSearchOption(prevState => {
                                return { ...prevState, lendingDepartment: selected };
                            });
                        }}
                    />
                    <RentalStatusSelect
                        selectedOption={selectedSearchOption.rentalStatus}
                        onChange={selected => {
                            setSelectedSearchOption(prevState => {
                                return { ...prevState, rentalStatus: selected };
                            });
                        }}
                    />
                </Search>

                <Card
                    header={{
                        title: t('Borrowed Asset List'),
                    }}
                >
                    <div className={'table-container'}>
                        <Table
                            key={tableKey}
                            data={{ ...assetImportList, pageSize: 20 }}
                            columns={columns}
                            onPageChange={handlePageChange}
                            loading={assetImportListInfo.isLoading}
                        />
                    </div>
                </Card>
            </div>
        </Page>
    );
};

export default AssetImport;
